import { otpSend } from '@/actions/ecom/login';
import { getStaticContent } from '@/actions/ecom/staticContent';
import { verifyLead } from '@/actions/verifyLead';
import PageLayout from '@/components/Layouts/PageLayout';
import VerifyOTP from '@/components/VerifyOTP';
import useFormRenderer, { FormField } from '@/hooks/useFormRenderer';
import useToast from '@/hooks/useToast';
import useTriggerRequest from '@/hooks/useTriggerRequest';
import AppBar from '@/shared/AppBar';
import Container from '@/shared/Container';
import Div from '@/shared/Div';
import Heading from '@/shared/Heading';
import Paragraph from '@/shared/Paragraph';
import { SeoData } from '@/types/seo';
import { GetServerSidePropsContext } from 'next';
import React, { Fragment, useEffect } from 'react';
import Image from '@/shared/Image';
import CheckCircle from '@/components/Icons/CheckCircleDemo';
import { CMS_COMPONENTS } from '@/constants/cms';
import APlusContent from '@/components/Product/APlusContent';
import useLayoutRenderer from '@/hooks/useLayoutRenderer';
import { MapType } from '@/types';
import Link from '@/shared/Link';
import BlueGradientBackground from '@/components/Icons/BlueGradientBackground';
import { useRouter } from 'next/router';
import { NOT_FOUND } from '@/constants/app';
import PhoneIcon from '@/components/Icons/PhoneIcon';
import DocumentIcon from '@/components/Icons/DocumentIcon';
import HomeIcon from '@/components/Icons/HomeIcon';
import { fetchPincodeData } from '@/actions/fetchPincodeData';
import useGTM from '@/hooks/useGTM';
import { GTMEventNames, SCREEN_NAMES } from '@/types/gtm';
import InformationCard from '@/shared/crisp/InformationCard';
import Script from 'next/script';
import Cookies from '@/utils/cookie';
import SingleImagesBanner from '@/shared/crisp/SingleImagesBanner';
import Map from '@/components/Map';
import is from '@/utils/is';
interface PageProps {
  config: {
    FORM_FIELDS: FormField[];
    EVENTS: any[];
    subTitlePhone: string;
    subTitle: string;
    title: string;
    success: any;
  };
  imageConfig: {
    MOB_PRODUCT_CATEGORY_IMAGE: MapType;
    DSK_PRODUCT_CATEGORY_IMAGE: MapType;
  };
  seo: SeoData;
  layout: any;
}

const STATIC_MAP = {
  [CMS_COMPONENTS.PRODUCT.A_PLUS_CONTENT]: APlusContent,
  [CMS_COMPONENTS.BANNERS.SINGE_IMAGE]: SingleImagesBanner,
};

const INVESTOR_DAY_URL = '/investor-relations/investor-day-registration';
const SELLER_REGISTRATION_URL: string = '/sell-with-earnsmart/register';

const SUCCESS_MESSAGE_PATHNAME_MAP: MapType = {
  [INVESTOR_DAY_URL]: 'Thank you for registration!',
  default: 'Thank you for reaching out!',
};

const BookDemoForm = ({ page }: { page: PageProps }) => {
  const { FORM_FIELDS = [], subTitlePhone, subTitle, title, EVENTS = [], success } = page.config;
  const { MOB_PRODUCT_CATEGORY_IMAGE, DSK_PRODUCT_CATEGORY_IMAGE } = page.imageConfig;

  const layout = useLayoutRenderer(page.layout, STATIC_MAP);
  const LEAD_TYPE_MAP: MapType = {
    '/bookafreehomedemo': 'BOOKADEMO',
    '/aquaguard/book-a-demo': 'AQUAGUARD_BOOKADEMO',
    '/forbesselectvacuumcleaner/book-a-demo': 'FORBESSELECT_BOOKADEMO',
    '/watersoftener/book-a-demo': 'WATERSOFTENER_BOOKADEMO',
    '/forbesairpurifier/book-a-demo': 'FORBESAIRPURIFIER_BOOKADEMO',
    '/forbesbuddypetgroomingkit/book-a-demo': 'FORBESBUDDYPET_BOOKADEMO',
    '/forbesprowatersolutions/book-a-demo': 'FORBESPROWATERSOLUTIONS_BOOKADEMO',
    [INVESTOR_DAY_URL]: 'INVESTOR_DAY_REGISTRATION',
    [SELLER_REGISTRATION_URL]: 'SELLER_REGISTRATION',
  };
  const [showSuccess, setShowSuccess] = React.useState(false);
  const [showOtpScreen, setShowOtpScreen] = React.useState(false);
  const fetch = (...args: any) => verifyLead.apply(null, args);
  const verifyLeadRequest = useTriggerRequest({ fetch });
  const pathName = useRouter().pathname;
  const { fireGTMEvent } = useGTM();
  const OTP = useTriggerRequest({ fetch: (...args: any) => otpSend.apply(null, args) });
  const { triggerError } = useToast();
  const submitHandler = async (data: any) => {
    try {
      const payload = { mobile_number: data.mobileNumber, email: data.email };

      if (data?.email && pathName === INVESTOR_DAY_URL) {
        payload['email'] = data.email;
      }

      await OTP.promisifyTrigger(payload);
      setShowOtpScreen(true);
    } catch (error: any) {
      triggerError({ message: error?.error, position: 'top' });
    }
  };

  const { tsx, form, updateFieldById } = useFormRenderer({
    fields: FORM_FIELDS,
    className: 'w-full grid grid-cols-2 gap-s5',
    submitHandler,
  });

  const onVerify = async (otp: string) => {
    try {
      verifyLeadRequest.reset();
      const { mobileNumber, productCategory, pincode, ...restFormValues } = form.getValues();
      const leadType = LEAD_TYPE_MAP[pathName];
      let leadData = {
        ...restFormValues,
        phone: mobileNumber,
        leadType: leadType,
        pincode: pincode,
      };
      if (productCategory && Array.isArray(productCategory) && productCategory.length > 0) {
        leadData.productCategory = productCategory.map((item: any) => item.id);
      } else if (productCategory && is.object(productCategory)) {
        leadData.productCategory = [productCategory.label];
      }
      const payload = {
        leadType: leadType,
        lead: leadData,
        verify: {
          mobileNumber,
          otp,
          userAgent: 'WEB',
        },
        utm: {
          utmSource: Cookies.get('utm_source'),
          utmMedium: Cookies.get('utm_medium'),
          utmChannel: Cookies.get('utm_channel'),
          utmCampaign: Cookies.get('utm_campaign'),
          utmTerm: Cookies.get('utm_term'),
        },
      };

      const response = await verifyLeadRequest.promisifyTrigger(payload);
      const responsePhoneNumber = response?.phone;
      setShowSuccess(true);
      if (pathName === SELLER_REGISTRATION_URL) {
        const { leadType, modifiedOn, productType, id, createdOn, utmParams, ...restResp } =
          response || {};
        fireGTMEvent(
          'SELLER_REGISTRATION_LEAD_SUBMITTED',
          {
            mobile_number: responsePhoneNumber,
            lead_type: LEAD_TYPE_MAP[pathName],
            screen_name: SCREEN_NAMES.BOOK_DEMO_PAGE,
            ...(restResp || {}),
          },
          {
            includeCustomer: false,
            includeScreen: false,
            includeSelectedDevice: false,
            includeSourceCTA: false,
            webengage: {
              fire: true,
              isDirect: true,
            },
          },
        );

        return;
      }

      if (pathName === INVESTOR_DAY_URL) {
        const { leadType, modifiedOn, productType, id, createdOn, utmParams, ...restResp } =
          response || {};
        fireGTMEvent(
          'INVESTOR_DAY_LEAD_SUBMITTED',
          {
            mobile_number: responsePhoneNumber,
            lead_type: LEAD_TYPE_MAP[pathName],
            screen_name: SCREEN_NAMES.BOOK_DEMO_PAGE,
            ...(restResp || {}),
            productCategory: response?.productCategory?.join(',') || '',
          },
          {
            includeCustomer: false,
            includeScreen: false,
            includeSelectedDevice: false,
            includeSourceCTA: false,
            webengage: {
              fire: true,
              isDirect: true,
            },
          },
        );
      } else {
        fireGTMEvent(
          GTMEventNames.LEAD_SUBMITTED,
          {
            mobile_number: responsePhoneNumber,
            lead_type: LEAD_TYPE_MAP[pathName],
            screen_name: SCREEN_NAMES.BOOK_DEMO_PAGE,
            ...(response || {}),
            productCategory: response?.productCategory?.join(',') || '',
          },
          {
            webengage: {
              fire: true,
              isDirect: true,
            },
          },
        );
      }
    } catch (error: any) {
      if (error?.detail?.code) {
        form.setError(error?.detail?.code, { message: error?.detail?.details });
        setShowOtpScreen(false);
        verifyLeadRequest.reset();
      }
      triggerError({ message: error?.error, position: 'top' });
    }
  };
  const mobileNumber = form.watch('mobileNumber') as any;
  const email = form.watch('email') as any;
  const productCategory = form.watch('productCategory') as any;
  const pincode = form.watch('pincode') as string;
  const pincodeData = useTriggerRequest({
    fetch: (...args: any) => fetchPincodeData.apply(null, args),
  });

  useEffect(() => {
    if (pincode && pincode.length === 6) {
      pincodeData
        .promisifyTrigger(pincode)
        .then((response) => {
          form.setValue('city', response.city);
          form.setValue('state', response.state);
        })
        .catch((error) => {
          triggerError?.({
            message: 'Please enter a different pincode.',
            position: 'top',
          });
          form.setValue('city', '');
          form.setValue('state', '');
        });
    } else {
      form.setValue('city', '');
      form.setValue('state', '');
    }
  }, [pincode]);

  const mobImage =
    MOB_PRODUCT_CATEGORY_IMAGE[productCategory?.length === 1 ? productCategory[0]?.id : 'default'];
  const isForbesProWaterSolutions = pathName === '/forbesprowatersolutions/book-a-demo';

  const dskImage =
    DSK_PRODUCT_CATEGORY_IMAGE[productCategory?.length === 1 ? productCategory[0]?.id : 'default'];
  useEffect(() => {
    updateFieldById('book_demo_form_submit', {
      isLoading: OTP.isLoading,
    });
  }, [OTP.isLoading]);

  const renderVerifyOTP = () => {
    if (showOtpScreen && !showSuccess) {
      let subTitle = `Enter the code we just sent to <b>${mobileNumber ?? ''}</b>`;
      if (pathName === INVESTOR_DAY_URL && email) {
        subTitle = `Enter the code we just sent to <b>${mobileNumber ?? ''}</b> and <b>${
          email ?? ''
        }</b>`;
      }

      return (
        <Fragment>
          <AppBar className="!-ml-6" onClick={() => setShowOtpScreen(false)} />
          <Div className="md:mt-s6">
            <VerifyOTP
              title="Verify your phone number or email"
              subTitle={subTitle}
              mobile={mobileNumber}
              email={email ?? undefined}
              onVerify={onVerify}
              verifyLeadRequest={verifyLeadRequest}
            />
          </Div>
        </Fragment>
      );
    }
    return null;
  };
  const renderSteps = () => {
    return (
      <Div className="relative w-full">
        <Div className="absolute left-7 top-12">
          <svg width="2" height="100%">
            <line x1="1" y1="0" x2="1" y2="100%" stroke="#A4CBFF" strokeWidth="1" />
          </svg>
        </Div>

        <Div className="flex flex-row items-center gap-s4 mb-8">
          <Div className="flex-shrink-0 z-10 ">
            <DocumentIcon />
          </Div>
          <Div className="flex flex-col gap-2">
            <Heading className="text-h-3 text-cti-primary">Step 1</Heading>
            <Paragraph className="text-body-md text-cti-primary">
              Fill up the form with your details.
            </Paragraph>
          </Div>
        </Div>

        <Div className="flex flex-row items-center gap-4 mb-8">
          <Div className="flex-shrink-0 z-10">
            <PhoneIcon width={58} height={58} />
          </Div>
          <Div className="flex flex-col gap-2">
            <Heading className="text-h-3 text-cti-primary">Step 2</Heading>
            <Paragraph className="text-body-md text-cti-primary">
              Our expert will connect with you over call
            </Paragraph>
          </Div>
        </Div>

        <Div className="flex flex-row items-center gap-4">
          <Div className="flex-shrink-0 z-10 ">
            <HomeIcon />
          </Div>
          <Div className="flex flex-col gap-2">
            <Heading className="text-h-3 text-cti-primary">Step 3</Heading>
            <Paragraph className="text-body-md text-cti-primary">
              Demo the product {!isForbesProWaterSolutions && ' in the comfort of your home!'}
            </Paragraph>
          </Div>
        </Div>
      </Div>
    );
  };

  const getSuccessTitle = () => {
    if (success?.title) return success.title;
    return SUCCESS_MESSAGE_PATHNAME_MAP[pathName] || SUCCESS_MESSAGE_PATHNAME_MAP['default'];
  };

  const getSuccessSubTitle = () => {
    if (success?.subTitle || success?.subTitle === '') return success.subTitle;
    if (pathName === INVESTOR_DAY_URL) {
      return (
        <Paragraph className="text-body-lg text-cti-primary ">
          {`We’re excited to have you join Eureka Forbes Investor Day ${new Date().getFullYear()}.`}
          <br />
          {`You’ll receive a confirmation email shortly with your event details.`}
        </Paragraph>
      );
    } else {
      return (
        <Paragraph className="text-body-lg text-cti-primary ">
          Our team will be in touch soon to schedule your demo.
          <br />
          {`We can't wait for you to try our products!`}
        </Paragraph>
      );
    }
  };

  const renderSuccessScreen = () => {
    if (!showSuccess) return null;

    return (
      <Fragment>
        <CheckCircle className="gap-s7 text-success" />
        <Div className="flex flex-col gap-s4">
          <Heading className=" mt-s7 text-h-2 md:text-hd-1 text-cti-primary">
            {getSuccessTitle()}
          </Heading>
          {getSuccessSubTitle()}
          {pathName === INVESTOR_DAY_URL || pathName === SELLER_REGISTRATION_URL ? null : (
            <Div className="flex flex-col items-start gap-s6 relative">
              <Heading className="mt-s6 text-h-3 md:text-hd-2 text-cti-primary">
                What to Expect Next:
              </Heading>
              {renderSteps()}
            </Div>
          )}
        </Div>
      </Fragment>
    );
  };

  const renderBookDemoForm = () => {
    if (showOtpScreen) return null;

    const subTitleParts = subTitle?.split('{phone}');

    return (
      <Div>
        <Div className="flex flex-col gap-s6 md:max-w-xl items-start self-start w-full">
          <Div className="flex flex-col gap-s4 w-full">
            <Paragraph className="text-h-2 md:text-hd-2">{title}</Paragraph>
            <Paragraph className="text-body-lg col-span-2">
              {subTitleParts[0]}{' '}
              <Link href={`tel:${subTitlePhone}`} className="text-cu-selected">
                {' '}
                {subTitlePhone}{' '}
              </Link>
              {subTitleParts[1]}
            </Paragraph>
            <Map
              list={EVENTS}
              className="bg-blue-100 rounded-cxs p-s4 text-cti-primary"
              render={(item) => (
                <Div key={item.key} className="flex flex-col gap-s3">
                  <Heading className="text-body-xl">
                    <b>{item.key}</b>:{item.value}
                  </Heading>
                </Div>
              )}
            />
          </Div>
          {tsx}
        </Div>
      </Div>
    );
  };
  return (
    <PageLayout seo={page.seo}>
      <BlueGradientBackground className="hidden md:block absolute top-0 left-0 -z-10 scale-x-[-1]" />
      <Div className="w-full md:hidden">
        <Image className="w-full h-auto object-contain" src={mobImage} alt="Book a Demo" />
      </Div>

      <Container className="flex flex-col w-full py-s6">
        <Div className="flex flex-col md:flex-row gap-s9 mb-s6">
          <Div className="hidden md:block w-full md:w-1/2">
            <Image
              className="w-full h-auto object-contain rounded-cxs"
              src={dskImage}
              alt="Book a Demo"
            />
          </Div>
          <Div className="md:w-1/2">
            {renderVerifyOTP()}
            {showSuccess ? renderSuccessScreen() : renderBookDemoForm()}
          </Div>
        </Div>
      </Container>
      {!showOtpScreen && !showSuccess && (
        <>
          {pathName === INVESTOR_DAY_URL || pathName === SELLER_REGISTRATION_URL ? null : (
            <>
              <Div className="w-full bg-blue-50 py-s6">
                <Container>
                  <Div className="flex flex-col md:flex-row gap-s6">
                    <Div className="md:w-1/2">
                      <Heading className="text-h-2 md:text-hd-1">
                        Book A Free {!isForbesProWaterSolutions && 'Home'} Demo in <br /> 3 Simple
                        Steps
                      </Heading>
                    </Div>
                    <Div className="md:w-1/2">{renderSteps()}</Div>
                  </Div>
                </Container>
              </Div>
              <Container className="flex flex-col mt-s9 pb-s6">
                <Heading className="text-h-2 md:text-hd-1 text-cu-selected">
                  Why Book a {!isForbesProWaterSolutions && 'Home'} Demo?
                </Heading>
                <Div className="flex flex-col md:flex-row gap-s6 mt-s6">
                  <Div className="w-full md:w-1/2">
                    <InformationCard
                      dataTestId="info-card-left"
                      title="Get Expert Guidance"
                      subTitle="Not sure which product is right for you? Our experts will assess your needs and help you choose the right product for your home."
                      icon={{
                        src: '',
                        data: {
                          attributes: {
                            url: '/sheild-dynamic-color.png',
                            width: 80,
                            height: 80,
                            alternativeText: 'Shield Icon',
                          } as any,
                        },
                      }}
                      ctas={[]}
                    />
                  </Div>
                  <Div className="w-full md:w-1/2">
                    <InformationCard
                      dataTestId="info-card-right"
                      title="Unlock Exclusive Offers"
                      subTitle="Enjoy special offers & discounts and get the best value while experiencing our products firsthand! "
                      icon={{
                        src: '',
                        data: {
                          attributes: {
                            url: '/gift-dynamic-color.png',
                            width: 80,
                            height: 80,
                            alternativeText: 'Gift Icon',
                          } as any,
                        },
                      }}
                      ctas={[]}
                    />
                  </Div>
                </Div>
              </Container>
            </>
          )}
        </>
      )}
      {layout}
      <Script
        src="//vxml4.plavxml.com/sited/ref/ctrk/1202-79321"
        strategy="afterInteractive"
      ></Script>
    </PageLayout>
  );
};

export const getServerSidePropsFunc =
  (url: string) => async (context: GetServerSidePropsContext) => {
    try {
      const page = await getStaticContent(url);
      if (!page.config?.FORM_FIELDS) {
        throw new Error('Required form configuration is missing');
      }
      return {
        props: {
          page,
        },
      };
    } catch (error) {
      return NOT_FOUND;
    }
  };

export const getServerSideProps = getServerSidePropsFunc('/bookafreehomedemo');

export default BookDemoForm;
